import React, {useState} from 'react'
import { useNavigate, Link } from 'react-router-dom';


import Button from '../components/buttons/Button';
import RadioButton from '../components/buttons/RadioButton';
import TextField from '../components/text/TextField';
import InputField from '../components/text/InputField';
import ContactUsQuotes from './ContactQuotes';

import { StaggerGroup, StaggerItem } from '../components/motion/Stagger';

import '../styles/contact/ContactUs.css';



import { useMediaQuery } from '../hooks/useMediaQuery';

const ContactForm = () => {

    // Mobile always shows feature cards in landscape style (icon beside
    // text) regardless of this product's own desktop orientation.
    const isMobileViewport = useMediaQuery('(max-width: 767px)');

  const [agreed, setAgreed] = useState(false);

  const navigate = useNavigate();

  // recaptcha Key
  const RECAPTCHA_SITE_KEY =
    "6LdyjZstAAAAAJ6T-OwNQUxTyqTe0LLvUBmqUyqy";

  const [formData, setFormData] = useState( {
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  function handleChange(e) {
    const { name, value } = e.target;
    setFormData(prevData => ({ ...prevData, [name]: value }));

    // Remove the error once the field is no longer empty
    if (value.trim()) {
        setErrors(prevErrors => {
            const updatedErrors = { ...prevErrors };

            delete updatedErrors[name];

            return updatedErrors;
        });
    }


  }

  function validateForm() {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Work email is required";
    }

    if (!formData.company.trim()) {
      newErrors.company = "Company is required";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }


    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    // Only reaches here if all fields are filled
    setIsSubmitting(true);

    try {

      if(!window.grecaptcha?.enterprise) {
        throw new Error("reCAPTCHA has not loaded");
      }

      const recaptchaToken = await new Promise((resolve, reject) => {

        window.grecaptcha.enterprise.ready(async () => {
                try {
                    const token =
                        await window.grecaptcha.enterprise.execute(
                            RECAPTCHA_SITE_KEY,
                            {
                                action: "contact_submit"
                            }
                        );

                    resolve(token);

                } catch (error) {
                    reject(error);
                }
            });

      })

      // https://contact-byshmdcobq-uc.a.run.app/
      const response = await fetch("https://contact-byshmdcobq-uc.a.run.app/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                ...formData,
                recaptchaToken,
                agreed: agreed
                
            })
        });

        if (!response.ok) {
            throw new Error("Failed to send email");
        }

        setFormData({
            firstName: "",
            lastName: "",
            email: "",
            company: "",
            phone: "",
            message: ""
        });

        setAgreed(false);

        navigate('/thank-you');

    } catch (error) {
      console.error(error);
      alert("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <StaggerGroup as="div" className='contact-page-container' staggerChildren={0.2} delayChildren={0.1}>
        {/* ── Left island: contact form ── */}
        <StaggerItem as="div" className='contact-form-container'>
            <div className='contact-form-title'>
                Chat with an expert
            </div>
            <div className='contact-form-subtitle'>
                A team member from NicheWare will reach out to discuss your needs.
            </div>
            
            <form onSubmit={handleSubmit}>
                <div className='contact-form'>
                    {/* Name row: correct tab order First → Last */}
                    <div className='contact-form-name-row'>
                        <InputField
                            title="First Name"
                            placeholder="First Name"
                            fieldName="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            error={errors.firstName}
                            />
                        <InputField
                            title="Last Name"
                            placeholder="Last Name"
                            fieldName="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            error={errors.lastName}
                        />
                    </div>
                    {/* Main row: Work Email → Company → Phone → Message */}
                    <div className='contact-form-main-row'>
                        <div className='contact-form-container-lhs'>
                            <InputField
                                title="Work Email"
                                placeholder="Work Email"
                                fieldName="email"
                                value={formData.email}
                                onChange={handleChange}
                                error={errors.email}
                            />
                            <InputField
                                title="Company"
                                placeholder="Company"
                                fieldName="company"
                                value={formData.company}
                                onChange={handleChange}
                                error={errors.company}
                            />
                            <InputField
                                title="Phone"
                                placeholder="Phone"
                                fieldName="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                error={errors.phone}
                            />
                        </div>
                        <div className='contact-form-container-rhs'>
                            <InputField
                                title="Message"
                                placeholder="Message"
                                type="textarea"
                                fieldName="message"
                                value={formData.message}
                                onChange={handleChange}
                                error={errors.message}
                            />
                        </div>
                    </div>
                </div>

                <div className='contact-form-consent-container'>

                    <TextField 

                        bold={false}
                        fontSize='0.875rem'
                        fontColor="#000000"
                    >
                        By submitting this form, you consent to NicheWare using the information provided to review and 
                        respond to your inquiry. Your information may be processed by service providers in the United States. 
                        For more information, see our {" "}
                        <Link to="/legal/privacy-policy" className="privacy-policy-link">
                            Privacy Policy.
                        </Link>
                    </TextField>

                    <RadioButton agreed={agreed} text="I consent to the processing of my information as described above."/>

                </div>

                <div className='contact-form-submit-container'>
                    <Button label="Submit" loading={isSubmitting}>
                      Connect with us
                    </Button>
                </div>
            </form>
        </StaggerItem>

        {/* ── Right panel: testimonial quote ── */}

        {!isMobileViewport && (
            <StaggerItem as="div" className='contact-info-container'>
                <ContactUsQuotes />
            </StaggerItem>
        )}


    </StaggerGroup>
  )
}

export default ContactForm
